import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Tab,
  Tabs,
  Badge,
  Alert,
  Snackbar,
  Paper,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  PlaylistAddCheck as ChecklistIcon,
  Comment as CommentIcon,
  DateRange as DateIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Assignment as TaskIcon,
} from '@mui/icons-material';
import {
  Task,
  TaskStatus,
  TaskPriority,
  TaskFilters,
} from '../types/tasks';

// Mock data for development
const mockTasks: Task[] = [
  {
    id: 'task-1',
    title: 'Review loan application for Tech Innovations LLC',
    description: 'Complete comprehensive review of loan application including credit check, financial analysis, and risk assessment.',
    status: 'in_progress',
    priority: 'high',
    category: 'Loan Review',
    reporter: { id: 'user-1', name: 'John Doe', email: 'john.doe@lender.ai' },
    assignee: { id: 'user-2', name: 'Jane Smith', email: 'jane.smith@lender.ai' },
    dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    estimatedHours: 8,
    actualHours: 3,
    tags: ['loan', 'review', 'urgent'],
    checklist: [
      { id: 'check-1', text: 'Verify business registration', completed: true, order: 0, completedAt: new Date().toISOString(), completedBy: 'user-2' },
      { id: 'check-2', text: 'Review financial statements', completed: true, order: 1, completedAt: new Date().toISOString(), completedBy: 'user-2' },
      { id: 'check-3', text: 'Perform credit check', completed: false, order: 2 },
      { id: 'check-4', text: 'Calculate risk score', completed: false, order: 3 },
      { id: 'check-5', text: 'Generate approval recommendation', completed: false, order: 4 },
    ],
    attachments: [
      {
        id: 'att-1',
        filename: 'financial-statements-2024.pdf',
        url: '#',
        size: 2048000,
        mimeType: 'application/pdf',
        uploadedAt: new Date().toISOString(),
        uploadedBy: 'user-1',
      },
    ],
    comments: [
      {
        id: 'comment-1',
        text: 'Initial review completed. Moving to credit check phase.',
        author: { id: 'user-2', name: 'Jane Smith' },
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      },
    ],
    configuration: {
      notificationsEnabled: true,
      allowFileUploads: true,
      maxFileSize: 10485760,
    },
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-1',
    updatedBy: 'user-2',
    version: 1,
  },
  {
    id: 'task-2',
    title: 'Update compliance documentation',
    description: 'Update all compliance documentation to reflect new regulatory requirements.',
    status: 'todo',
    priority: 'medium',
    category: 'Compliance',
    reporter: { id: 'user-3', name: 'Mike Johnson', email: 'mike.johnson@lender.ai' },
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    estimatedHours: 16,
    tags: ['compliance', 'documentation'],
    checklist: [
      { id: 'check-6', text: 'Review new regulations', completed: false, order: 0 },
      { id: 'check-7', text: 'Update policy documents', completed: false, order: 1 },
      { id: 'check-8', text: 'Review with legal team', completed: false, order: 2 },
    ],
    configuration: {
      notificationsEnabled: true,
      requireApproval: true,
    },
    createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-3',
    version: 1,
  },
];

const statusColors: Record<TaskStatus, string> = {
  todo: '#9e9e9e',
  in_progress: '#2196f3',
  review: '#ff9800',
  completed: '#4caf50',
  archived: '#607d8b',
};

const priorityColors: Record<TaskPriority, string> = {
  low: '#4caf50',
  medium: '#ff9800',
  high: '#f44336',
  urgent: '#9c27b0',
};

const TaskManagerPage: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>(mockTasks);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>(mockTasks);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState<TaskFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const applyFilters = useCallback(() => {
    let filtered = [...tasks];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(search) ||
        task.description?.toLowerCase().includes(search) ||
        task.category?.toLowerCase().includes(search) ||
        task.tags?.some(tag => tag.toLowerCase().includes(search))
      );
    }

    if (filters.status) {
      const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
      filtered = filtered.filter(task => statuses.includes(task.status));
    }

    if (filters.priority) {
      const priorities = Array.isArray(filters.priority) ? filters.priority : [filters.priority];
      filtered = filtered.filter(task => priorities.includes(task.priority));
    }

    if (filters.assigneeId) {
      filtered = filtered.filter(task => task.assignee?.id === filters.assigneeId);
    }

    if (filters.category) {
      filtered = filtered.filter(task => task.category === filters.category);
    }

    setFilteredTasks(filtered);
  }, [tasks, searchTerm, filters]);

  React.useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleDeleteTask = (taskId: string) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
    setSelectedTask(null);
    setSnackbar({ open: true, message: 'Task deleted successfully!', severity: 'success' });
  };

  const handleToggleChecklistItem = (taskId: string, itemId: string, completed: boolean) => {
    setTasks(prev => prev.map(task => 
      task.id === taskId 
        ? {
            ...task,
            checklist: task.checklist?.map(item =>
              item.id === itemId
                ? {
                    ...item,
                    completed,
                    completedAt: completed ? new Date().toISOString() : undefined,
                    completedBy: completed ? 'current-user' : undefined,
                  }
                : item
            ),
            updatedAt: new Date().toISOString(),
            version: task.version + 1,
          }
        : task
    ));
  };

  const handleAddChecklistItem = (taskId: string, text: string) => {
    setTasks(prev => prev.map(task => 
      task.id === taskId 
        ? {
            ...task,
            checklist: [
              ...(task.checklist || []),
              {
                id: `check-${Date.now()}`,
                text,
                completed: false,
                order: (task.checklist || []).length,
              }
            ],
            updatedAt: new Date().toISOString(),
            version: task.version + 1,
          }
        : task
    ));
  };

  const getTaskStats = () => {
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const inProgress = tasks.filter(t => t.status === 'in_progress').length;
    const overdue = tasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'completed').length;
    
    return { total, completed, inProgress, overdue };
  };

  const stats = getTaskStats();

  const renderTaskCard = (task: Task) => (
    <Card key={task.id} sx={{ mb: 2, cursor: 'pointer' }} onClick={() => setSelectedTask(task)}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {task.title}
          </Typography>
          <Box display="flex" gap={1}>
            <Chip
              label={task.status.replace('_', ' ')}
              size="small"
              sx={{ bgcolor: statusColors[task.status], color: 'white' }}
            />
            <Chip
              label={task.priority}
              size="small"
              sx={{ bgcolor: priorityColors[task.priority], color: 'white' }}
            />
          </Box>
        </Box>
        
        {task.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {task.description.substring(0, 150)}...
          </Typography>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            {task.assignee && (
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2">{task.assignee.name}</Typography>
              </Box>
            )}
            {task.dueDate && (
              <Box display="flex" alignItems="center" gap={1}>
                <DateIcon fontSize="small" color="action" />
                <Typography variant="body2" color={new Date(task.dueDate) > new Date() ? 'text.secondary' : 'error'}>
                  {new Date(task.dueDate).toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            {task.attachments && task.attachments.length > 0 && (
              <Badge badgeContent={task.attachments.length} color="primary">
                <AttachFileIcon fontSize="small" color="action" />
              </Badge>
            )}
            {task.checklist && task.checklist.length > 0 && (
              <Badge badgeContent={`${task.checklist.filter(i => i.completed).length}/${task.checklist.length}`} color="primary">
                <ChecklistIcon fontSize="small" color="action" />
              </Badge>
            )}
            {task.comments && task.comments.length > 0 && (
              <Badge badgeContent={task.comments.length} color="primary">
                <CommentIcon fontSize="small" color="action" />
              </Badge>
            )}
          </Box>
        </Box>

        {task.checklist && task.checklist.length > 0 && (
          <Box>
            <LinearProgress
              variant="determinate"
              value={(task.checklist.filter(i => i.completed).length / task.checklist.length) * 100}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Progress: {task.checklist.filter(i => i.completed).length} of {task.checklist.length} items completed
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderTaskDetails = () => {
    if (!selectedTask) return null;

    return (
      <Dialog open={!!selectedTask} onClose={() => setSelectedTask(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{selectedTask.title}</Typography>
            <Box>
              <IconButton onClick={() => {
                setSnackbar({ open: true, message: 'Task editing not implemented yet', severity: 'info' });
              }}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDeleteTask(selectedTask.id)} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
            <Tab label="Details" />
            <Tab label="Checklist" />
            <Tab label="Attachments" />
            <Tab label="Comments" />
            <Tab label="Configuration" />
          </Tabs>

          {activeTab === 0 && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedTask.description}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip label={selectedTask.status} sx={{ bgcolor: statusColors[selectedTask.status], color: 'white' }} />
                </Grid>
                <Grid size={6}>
                  <Typography variant="subtitle2">Priority</Typography>
                  <Chip label={selectedTask.priority} sx={{ bgcolor: priorityColors[selectedTask.priority], color: 'white' }} />
                </Grid>
                <Grid size={6}>
                  <Typography variant="subtitle2">Assignee</Typography>
                  <Typography>{selectedTask.assignee?.name || 'Unassigned'}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="subtitle2">Due Date</Typography>
                  <Typography>{selectedTask.dueDate ? new Date(selectedTask.dueDate).toLocaleDateString() : 'No due date'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Task Checklist</Typography>
              <List>
                {selectedTask.checklist?.map((item) => (
                  <ListItem key={item.id} dense>
                    <ListItemIcon>
                      <Checkbox
                        checked={item.completed}
                        onChange={(e) => handleToggleChecklistItem(selectedTask.id, item.id, e.target.checked)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      secondary={item.completed ? `Completed ${item.completedAt ? new Date(item.completedAt).toLocaleDateString() : ''}` : undefined}
                      sx={{ textDecoration: item.completed ? 'line-through' : 'none' }}
                    />
                  </ListItem>
                ))}
              </List>
              <TextField
                fullWidth
                size="small"
                placeholder="Add checklist item..."
                onKeyPress={(e) => {
                  const target = e.target as HTMLInputElement;
                  if (e.key === 'Enter' && target.value.trim()) {
                    handleAddChecklistItem(selectedTask.id, target.value.trim());
                    target.value = '';
                  }
                }}
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>File Attachments</Typography>
              {selectedTask.attachments?.map((attachment) => (
                <Paper key={attachment.id} sx={{ p: 2, mb: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="subtitle2">{attachment.filename}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(attachment.size / 1024 / 1024).toFixed(2)} MB • Uploaded {new Date(attachment.uploadedAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Button size="small" href={attachment.url} target="_blank">
                      Download
                    </Button>
                  </Box>
                </Paper>
              ))}
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Upload File
                <input type="file" hidden />
              </Button>
            </Box>
          )}

          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Comments</Typography>
              {selectedTask.comments?.map((comment) => (
                <Paper key={comment.id} sx={{ p: 2, mb: 2 }}>
                  <Box display="flex" gap={2}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {comment.author.name.charAt(0)}
                    </Avatar>
                    <Box flexGrow={1}>
                      <Typography variant="subtitle2">{comment.author.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(comment.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {comment.text}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Add a comment..."
                variant="outlined"
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          {activeTab === 4 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Task Configuration</Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <FormControl component="fieldset">
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Settings</Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        🔔 Notifications: {selectedTask.configuration?.notificationsEnabled ? 'Enabled' : 'Disabled'}
                      </Typography>
                      <Typography variant="body2">
                        📁 File Uploads: {selectedTask.configuration?.allowFileUploads ? 'Allowed' : 'Disabled'}
                      </Typography>
                      <Typography variant="body2">
                        ✅ Require Approval: {selectedTask.configuration?.requireApproval ? 'Yes' : 'No'}
                      </Typography>
                      {selectedTask.configuration?.maxFileSize && (
                        <Typography variant="body2">
                          📏 Max File Size: {(selectedTask.configuration.maxFileSize / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      )}
                    </Stack>
                  </FormControl>
                </Grid>
              </Grid>
              <Button
                variant="outlined"
                onClick={() => {
                  setSnackbar({ open: true, message: 'Configuration editing not implemented yet', severity: 'info' });
                }}
                sx={{ mt: 2 }}
              >
                Edit Configuration
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTask(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Task Manager
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSnackbar({ open: true, message: 'Task creation dialog not implemented yet', severity: 'info' });
            }}
          >
            Create Task
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <TaskIcon color="primary" />
                  <Box>
                    <Typography variant="h4">{stats.total}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Tasks</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <CheckCircleIcon color="success" />
                  <Box>
                    <Typography variant="h4">{stats.completed}</Typography>
                    <Typography variant="body2" color="text.secondary">Completed</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <ScheduleIcon color="info" />
                  <Box>
                    <Typography variant="h4">{stats.inProgress}</Typography>
                    <Typography variant="body2" color="text.secondary">In Progress</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <WarningIcon color="error" />
                  <Box>
                    <Typography variant="h4">{stats.overdue}</Typography>
                    <Typography variant="body2" color="text.secondary">Overdue</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters and Search */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as TaskStatus }))}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="todo">To Do</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="review">Review</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filters.priority || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value as TaskPriority }))}
                  label="Priority"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                  label="Category"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Loan Review">Loan Review</MenuItem>
                  <MenuItem value="Compliance">Compliance</MenuItem>
                  <MenuItem value="Documentation">Documentation</MenuItem>
                  <MenuItem value="Analysis">Analysis</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setFilters({})}
                startIcon={<FilterIcon />}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Task List */}
        <Grid container spacing={3}>
          {filteredTasks.map(task => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={task.id}>
              {renderTaskCard(task)}
            </Grid>
          ))}
        </Grid>

        {filteredTasks.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <TaskIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No tasks found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {searchTerm || Object.keys(filters).length > 0
                ? 'Try adjusting your search or filters'
                : 'Create your first task to get started'
              }
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                setSnackbar({ open: true, message: 'Task creation dialog not implemented yet', severity: 'info' });
              }}
              startIcon={<AddIcon />}
            >
              Create Task
            </Button>
          </Paper>
        )}

        {/* Task Details Dialog */}
        {renderTaskDetails()}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
  );
};

export default TaskManagerPage;